import { Box } from '@mui/material'
import Highcharts from 'highcharts'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import api from '../../../../../api'
import { useDefaultComponentTools } from '../../../../../hooks/useDefaultComponentTools'
import { selectAssetTitle } from '../../../../../store/redusers/assets/selectors'
import { setExecutionEntryRate } from '../../../../../store/redusers/orders/actions'
import {
	selectDelayPricer,
	selectExecutionEntryRate,
	selectNewTradeType
} from '../../../../../store/redusers/orders/selectors'
import {
	selectPricerLink,
	selectPricerTradingViewPort,
	selectRiskData,
	selectUserId
} from '../../../../../store/redusers/user/selectors'

const HighChart = ({asset, oldUserRate, pendingAskBid}) => {
	const {alert, dispatch, theme, t} = useDefaultComponentTools(true)
	const chartContainerRef = useRef()
	const assetName = useSelector(selectAssetTitle(asset.assetType, asset.assetSoId))
	const userId = useSelector(selectUserId)
	const pendingChartAskBid = useSelector(selectNewTradeType)
	const delayPricer = useSelector(selectDelayPricer)
	const askBid = pendingAskBid || pendingChartAskBid
	const plotColor = askBid === 'sell' ? theme.palette.red : theme.palette.green
	const riskData = useSelector(selectRiskData)
	const pricerLink = useSelector(selectPricerLink)
	const pricerTradingViewPort = useSelector(selectPricerTradingViewPort)
	const executionEntryRate = useSelector(selectExecutionEntryRate)
	const riskDataPart = riskData['spread_'+asset.type]
	const brandName = riskData.brand
	const [isChart, setIsChart] = useState(false)
	const [lastRate,  setLastRate] = useState(null)
	const [data,  setData] = useState(null)
	const isDark = theme.palette.mode === 'dark'
	const [highcharts, setHighcharts] = useState(null)


	const draggablePlotLine = (axis, plotLineId) => {
		let clickX, clickY
		const getPlotLine = () => {
			for (let i = 0; i < axis.plotLinesAndBands.length; i++) {
				if (axis.plotLinesAndBands[i].id === plotLineId) {
					return axis.plotLinesAndBands[i]
				}
			}
		}

		const getValue = () => {
			const plotLine = getPlotLine()
			const translation = axis.horiz ? plotLine.svgElem.translateX : plotLine.svgElem.translateY
			let new_value = axis.toValue(translation) - axis.toValue(0) + plotLine.options.value
			new_value = Math.max(axis.min, Math.min(axis.max, new_value))
			return new_value
		}

		const drag_start = function (e) {
			document.addEventListener('mousemove', drag_step)
			document.addEventListener('mouseup',  drag_stop)
			const plotLine = getPlotLine()
			clickX = e.pageX - plotLine.svgElem.translateX
			clickY = e.pageY - plotLine.svgElem.translateY
			if (plotLine.options.onDragStart) {
				plotLine.options.onDragStart(getValue())
			}
		}

		const drag_step = function (e) {
			const plotLine = getPlotLine()
			let new_translation = axis.horiz ? e.pageX - clickX : e.pageY - clickY
			let new_value = axis.toValue(new_translation) - axis.toValue(0) + plotLine.options.value
			new_value = Math.max(axis.min, Math.min(axis.max, new_value))
			new_translation = axis.toPixels(new_value + axis.toValue(0) - plotLine.options.value)
			plotLine.svgElem.translate(
				axis.horiz ? new_translation : 0,
				axis.horiz ? 0 : new_translation)
			if (plotLine.options.onDragChange) {
				plotLine.options.onDragChange(new_value)
			}
		}

		const drag_stop = () => {
			document.removeEventListener('mousemove', drag_step)
			document.removeEventListener('mouseup', drag_stop)
			const plotLine = getPlotLine()
			const plotLineOptions = plotLine.options
			if (Object.prototype.hasOwnProperty.call(plotLine.svgElem, 'translateX')) {
				plotLineOptions.value = getValue()
				axis.removePlotLine(plotLineOptions.id)
				axis.addPlotLine(plotLineOptions)
				if (plotLineOptions.onDragFinish) {
					plotLineOptions.onDragFinish(plotLineOptions.value)
				}
			}
			getPlotLine().svgElem.css({'cursor': 'pointer'}).translate(0, 0).on('mousedown', drag_start)
		}
		drag_stop()
	}

	const getPendingChart = async () => {
		let data = []
		let nowTimeInSeconds = Math.floor(Date.now() / 1000)
		let typeData = 'l'
		if (askBid === 'buy') {
			typeData = 'h'
		}
		try {
			let res = await api.assets.getDataToHighChart(pricerLink, pricerTradingViewPort,
				encodeURIComponent(assetName),
				nowTimeInSeconds, asset.assetType,
				riskDataPart, delayPricer, userId, brandName)
			if (!res.data || 0 === res.data.length) {
				if (Array.isArray(res.data)) {
					alert.error('No Valid rates. Please try again later.')
					return
				}
				alert.error(t('Request failed. Please try again.'))
				return
			}
			if (res.data.length === 0) {
				// eslint-disable-next-line
				console.log('NO RATES')
				return false;
			}
			for (let i = 0; i < res.data[typeData].length; ++i) {
				data.push([ parseInt(res.data.t[i] * 1000), parseFloat(res.data[typeData][i]) ]);
			}
			setData(data)
			let rate
			if (oldUserRate) {
				rate = parseFloat(oldUserRate)
			} else {
				rate = data[data.length - 1][1]
			}
			setLastRate(rate)
			setIsChart(true)
		} catch (e) {
			// eslint-disable-next-line
			console.log('highchart error:', e)
		}
	}
	useEffect(() => {getPendingChart()},[asset, askBid])

	useEffect(() => {
		if(!isChart) return
		const myChart = Highcharts.chart(chartContainerRef.current, {
			chart: {
				backgroundColor: theme.palette.second,
				events: {
					load: function () {
						this.xAxis[0].update({
							tickPositioner: function () {
								let ret = []
								this.series[0].xData.forEach(function (elem) {
									ret.push(elem)
								})
								return ret
							}
						})
					}
				},
				style: {
					color: theme.palette.text.primary,
					fontFamily: 'SF Pro Display'
				},
				type: 'spline'
			},
			credits: {
				enabled: false
			},
			exporting: {
				enabled: false
			},
			global: {
				useUTC: true
			},
			legend: {
				enabled: false
			},
			plotOptions: {
				series: {
					color: '#999',
					marker: {
						enabled: true,
						symbol: 'diamond'
					}
				}
			},
			series: [{
				data,
				id: asset.assetSoId,
				name: 'Price'
			}],
			title: {
				text: ''
			},
			tooltip: {
				backgroundColor:  theme.palette.third,
				borderRadius: 5,
				borderWidth: 0,
				headerFormat: '<b>{point.x:%Y-%m-%d %H:%M:%S}</b><br/>',
				pointFormat: 'Price: {point.y:.7f}',
				positioner: function () {
					return { x: 110, y: 150 };
				},
				shadow: false,
				style: {
					color: theme.palette.text.primary,
					fontFamily: 'SF Pro Display'
				}
			},
			xAxis: {
				labels: {
					enabled: false
				},
				lineWidth: 0,
				tickWidth: 0
			},
			yAxis: [
				{
					endOnTick: true,
					gridLineColor: theme.palette.background,
					labels: {
						style: {
							color: theme.palette.text.primary,
							fontFamily: 'SF Pro Display'
						}
					},
					maxPadding: .01,
					minRange: 0.00000001,
					plotLines: [
						{
							color: plotColor,
							id: 'movingLine',
							onDragChange: function (new_value) {
								setLastRate(parseFloat(new_value).toFixed(9))
							},
							onDragFinish: function (new_value) {
								setLastRate(parseFloat(new_value).toFixed(9))
								myChart.redraw()
							},
							value: lastRate,
							width: 6,
							zIndex: 9999
						}
					],
					startOnTick: true,
					title: {
						text: ''
					}
				},
				{
					opposite: true,
					tickPositioner: function () {
						let data = this.chart.yAxis[0].series[0].processedYData
						return [ parseFloat(data[data.length - 1]).toFixed(9) ]
					},
					title: {
						text: ''
					}
				}
			]
		})
		setHighcharts(myChart)
		draggablePlotLine(myChart.yAxis[0], 'movingLine')
	},[isChart, isDark, asset, pendingChartAskBid])

	useEffect(() => {
		if(!highcharts || lastRate === executionEntryRate) return
		highcharts.yAxis[0].options.plotLines[0].value = +executionEntryRate
		highcharts.yAxis[0].update()
	}, [executionEntryRate])

	useEffect(() => {
		dispatch(setExecutionEntryRate(lastRate))
	}, [dispatch, lastRate])

	return (
		<Box sx={{borderRadius: 1}}
			ref={chartContainerRef}
			className='chart-container'
		>
		</Box>
	)
}

HighChart.propTypes = {
	asset: PropTypes.object,
	oldUserRate: PropTypes.number,
	pendingAskBid: PropTypes.string
}
export default HighChart
